import React, { useState, useEffect } from 'react';
import { collection, getDocs, doc, setDoc, deleteDoc } from 'firebase/firestore';
import { db, firebaseConfig } from '../firebase/config';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, connectAuthEmulator } from 'firebase/auth';
import { toast } from 'react-toastify';
import { ensureRolePermissionsDoc, getRolePermissions, setRolePermissions, updateUserRole } from '../firebase/services';
import { DEFAULT_ROLE_PERMISSIONS, mergePermissionsMaps, PERMISSIONS } from '../Utils/permissions';
import './Admin.css';

export default function ManageUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [roleEdits, setRoleEdits] = useState({});

  const [selectedRole, setSelectedRole] = useState('moderator');
  const [rolePermissions, setRolePermissionsState] = useState({});
  const [loadingRolePerms, setLoadingRolePerms] = useState(false);
  const [savingRolePerms, setSavingRolePerms] = useState(false);

  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    role: 'shipping'
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  useEffect(() => {
    // Seed default role docs if missing (admin can edit later)
    (async () => {
      try {
        const roles = ['admin', 'moderator', 'shipping', 'shipping_company'];
        for (const r of roles) {
          // eslint-disable-next-line no-await-in-loop
          await ensureRolePermissionsDoc(r, DEFAULT_ROLE_PERMISSIONS[r] || {});
        }
      } catch (e) {
        // Non-fatal; Firestore rules might block non-admin or network issues.
        console.warn('Unable to ensure role permission docs:', e);
      }
    })();
  }, []);

  useEffect(() => {
    loadRolePermissions(selectedRole);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRole]);

  const fetchUsers = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, 'users'));
      const usersList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setUsers(usersList);
      const edits = {};
      usersList.forEach((u) => {
        edits[u.id] = u.role || 'shipping';
      });
      setRoleEdits(edits);
    } catch (error) {
      console.error("Error fetching users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const loadRolePermissions = async (roleId) => {
    setLoadingRolePerms(true);
    try {
      const remote = await getRolePermissions(roleId);
      const merged = mergePermissionsMaps(DEFAULT_ROLE_PERMISSIONS[roleId] || {}, remote || {});
      setRolePermissionsState(merged);
    } catch (error) {
      console.error('Error loading role permissions:', error);
      toast.error('Failed to load role permissions');
      setRolePermissionsState(DEFAULT_ROLE_PERMISSIONS[roleId] || {});
    } finally {
      setLoadingRolePerms(false);
    }
  };

  const handleToggleRolePermission = (permissionKey) => {
    setRolePermissionsState((prev) => ({
      ...(prev || {}),
      [permissionKey]: !prev?.[permissionKey]
    }));
  };

  const handleSaveRolePermissions = async () => {
    setSavingRolePerms(true);
    try {
      await setRolePermissions(selectedRole, rolePermissions || {});
      toast.success('Role permissions saved');
    } catch (error) {
      console.error('Error saving role permissions:', error);
      toast.error('Failed to save role permissions');
    } finally {
      setSavingRolePerms(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    try {
      // Initialize secondary app to create user without logging out admin
      const appName = `secondaryApp-${Date.now()}`;
      const secondaryApp = initializeApp(firebaseConfig, appName);
      const secondaryAuth = getAuth(secondaryApp);

      // Connect to emulator if running locally
      if (window.location.hostname === "localhost" || window.location.hostname === "127.0.0.1") {
        connectAuthEmulator(secondaryAuth, "http://127.0.0.1:9099");
      }

      const userCredential = await createUserWithEmailAndPassword(
        secondaryAuth, 
        newUser.email, 
        newUser.password
      );
      
      const uid = userCredential.user.uid;

      // Save user details to Firestore
      await setDoc(doc(db, 'users', uid), {
        name: newUser.name,
        email: newUser.email,
        role: newUser.role,
        createdAt: new Date().toISOString()
      });

      toast.success("User created successfully");
      setNewUser({ name: '', email: '', password: '', role: 'shipping' });
      fetchUsers();
      
    } catch (error) {
      console.error("Error creating user:", error);
      toast.error(error.message);
    }
  };

  const handleDeleteUser = async (userId) => {
      if(window.confirm("Are you sure you want to delete this user? Note: This only removes them from the database, not from Authentication.")){
          try {
              await deleteDoc(doc(db, 'users', userId));
              toast.success("User removed from database");
              fetchUsers();
          } catch (error) {
              console.error("Error deleting user:", error);
              toast.error("Failed to delete user");
          }
      }
  }

  const handleSaveUserRole = async (userId) => {
    try {
      const newRoleValue = roleEdits[userId];
      await updateUserRole(userId, newRoleValue);
      toast.success('User role updated');
      fetchUsers();
    } catch (error) {
      console.error('Error updating user role:', error);
      toast.error('Failed to update user role');
    }
  };

  if (loading) {
    return (
      <div className="admin-container" style={{display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh'}}>
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <div>
          <h2>Manage Users</h2>
          <p>Create and manage staff accounts</p>
        </div>
      </div>

      <div className="admin-card">
        <div className="section-title">
          <ion-icon name="key-outline"></ion-icon> Role Permissions
        </div>

        <div className="form-grid">
          <div className="form-group">
            <label>Role</label>
            <select
              className="custom-select"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
            >
              <option value="admin">Admin</option>
              <option value="moderator">Moderator</option>
              <option value="shipping">Shipping</option>
              <option value="shipping_company">Shipping Company</option>
            </select>
          </div>
        </div>

        {loadingRolePerms ? (
          <div style={{ padding: '12px 0', color: '#6b7280' }}>Loading role permissions…</div>
        ) : (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', paddingTop: '8px' }}>
            {[
              { key: PERMISSIONS.ORDERS_VIEW, label: 'Orders: View' },
              { key: PERMISSIONS.ORDERS_UPDATE, label: 'Orders: Update' },
              { key: PERMISSIONS.DELIVERY_VIEW, label: 'Delivery: View' },
              { key: PERMISSIONS.DELIVERY_UPDATE, label: 'Delivery: Update' },
              { key: PERMISSIONS.PRODUCTS_MANAGE, label: 'Products: Manage' },
              { key: PERMISSIONS.CATEGORIES_MANAGE, label: 'Categories: Manage' },
              { key: PERMISSIONS.BRANDS_MANAGE, label: 'Brands: Manage' },
              { key: PERMISSIONS.DISCOUNTS_MANAGE, label: 'Discounts: Manage' },
              { key: PERMISSIONS.SHIPPING_RATES_MANAGE, label: 'Shipping Rates: Manage' },
              { key: PERMISSIONS.USERS_MANAGE, label: 'Users: Manage' },
              { key: PERMISSIONS.HERO_MANAGE, label: 'Hero: Manage' },
              { key: PERMISSIONS.APPEARANCE_MANAGE, label: 'Appearance: Manage' },
              { key: PERMISSIONS.PAGES_MANAGE, label: 'Pages: Manage' }
            ].map((item) => (
              <label key={item.key} style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px', border: '1px solid #e5e7eb', borderRadius: '10px' }}>
                <input
                  type="checkbox"
                  checked={!!rolePermissions?.[item.key]}
                  onChange={() => handleToggleRolePermission(item.key)}
                />
                <span style={{ fontWeight: 600, color: '#111827' }}>{item.label}</span>
              </label>
            ))}
          </div>
        )}

        <div className="form-actions" style={{ marginTop: '14px' }}>
          <button type="button" className="btn-primary" onClick={handleSaveRolePermissions} disabled={savingRolePerms}>
            <ion-icon name="save-outline"></ion-icon> {savingRolePerms ? 'Saving…' : 'Save Role Permissions'}
          </button>
        </div>
      </div>
      
      <div className="admin-card">
        <div className="section-title">
          <ion-icon name="person-add-outline"></ion-icon> Add New User
        </div>
        <form onSubmit={handleAddUser}>
          <div className="form-grid">
            <div className="form-group">
              <label>Name</label>
              <input
                type="text"
                className="custom-input"
                value={newUser.name}
                onChange={(e) => setNewUser({...newUser, name: e.target.value})}
                placeholder="Full Name"
                required
              />
            </div>
            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                className="custom-input"
                value={newUser.email}
                onChange={(e) => setNewUser({...newUser, email: e.target.value})}
                placeholder="email@example.com"
                required
              />
            </div>
          </div>
          <div className="form-grid">
            <div className="form-group">
              <label>Password</label>
              <input
                type="password"
                className="custom-input"
                value={newUser.password}
                onChange={(e) => setNewUser({...newUser, password: e.target.value})}
                placeholder="••••••••"
                required
              />
            </div>
            <div className="form-group">
              <label>Role</label>
              <select
                className="custom-select"
                value={newUser.role}
                onChange={(e) => setNewUser({...newUser, role: e.target.value})}
              >
                <option value="shipping">Shipping</option>
                <option value="shipping_company">Shipping Company</option>
                <option value="admin">Admin</option>
                <option value="moderator">Moderator</option>
              </select>
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="btn-primary">
              <ion-icon name="add-outline"></ion-icon> Create User
            </button>
          </div>
        </form>
      </div>

      <div className="admin-card" style={{padding: 0, overflow: 'hidden'}}>
        <div className="table-container" style={{border: 'none'}}>
          <table className="admin-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email</th>
                <th>Role</th>
                <th style={{textAlign: 'right'}}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.length === 0 ? (
                <tr>
                  <td colSpan="4" style={{textAlign: 'center', padding: '40px', color: '#6b7280'}}>
                    No users found.
                  </td>
                </tr>
              ) : (
                users.map(user => (
                  <tr key={user.id}>
                    <td style={{fontWeight: 500}}>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <select
                        className="custom-select"
                        value={roleEdits[user.id] || user.role || 'shipping'}
                        onChange={(e) => setRoleEdits((prev) => ({ ...prev, [user.id]: e.target.value }))}
                        style={{ minWidth: '170px' }}
                      >
                        <option value="admin">Admin</option>
                        <option value="moderator">Moderator</option>
                        <option value="shipping">Shipping</option>
                        <option value="shipping_company">Shipping Company</option>
                      </select>
                    </td>
                    <td style={{textAlign: 'right'}}>
                      <button
                        onClick={() => handleSaveUserRole(user.id)}
                        className="action-btn"
                        title="Save Role"
                        style={{ marginRight: '8px' }}
                      >
                        <ion-icon name="save-outline"></ion-icon>
                      </button>
                      <button 
                        onClick={() => handleDeleteUser(user.id)} 
                        className="action-btn delete"
                        title="Delete User"
                      >
                        <ion-icon name="trash-outline"></ion-icon>
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
